import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { FaChartLine } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import academicStyles from "../../../pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import dashStyles from "./AiDashboard.module.css";
import CustomBarChart from "../../../components/Chart/CustomBarChart";
import ScorePredictionChart from "../../../components/Chart/ScorePredictionChart/ScorePredictionChart";
import CustomDonutChart from "../../../components/Chart/DrilldownChart/CustomDonutChart";
import CustomCalendar from "../../../components/CustomCalendar/CustomCalendar";
import HorizontalBarChart from "../../../components/Chart/Horizontalbarchart/Horizontalbarchart";
import LineChart from "../../../components/Chart/LineChart/LineChart";
import AlertsList from "../../../components/Chart/AlertsList/AlertsList";
import AxiosInstance, { TokenService } from "../../../services/Api";
// Adjust the path below to wherever CustomLoader.jsx lives in your tree.
import CustomLoader from "../../../components/CustomLoader/CustomLoader";

/* ─────────────────────────────────────────────────────────────────────────
 *  Module-level constants & helpers
 * ───────────────────────────────────────────────────────────────────────── */
const FILTER_OPTIONS = [
  "All",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_LABEL_TO_NUMBER = {
  All: "All",
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12,
};

/* Backend status codes → UI status strings.
 * 0 = Absent, 1 = Present, 2 = Leave, 3 = Holiday, 4 = No working day (blank). */
const STATUS_CODE_MAP = {
  0: "absent",
  1: "present",
  2: "leave",
  3: "holiday",
};

/* API component.key → UI breakdown key */
const HEALTH_KEY_MAP = {
  exam: "examMarks",
  attendance: "attendance",
  lapsed: "assessmentRemedial",
  session: "applicationUsage",
};

const pad2 = (n) => String(n).padStart(2, "0");

const currentYyyymm = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

const statusFromScore = (score) => {
  if (score >= 75) return "green";
  if (score >= 50) return "yellow";
  return "red";
};

const isAbortError = (err) =>
  err?.name === "AbortError" ||
  err?.name === "CanceledError" ||
  err?.code === "ERR_CANCELED";

/* ─────────────────────────────────────────────────────────────────────────
 *  Component
 * ───────────────────────────────────────────────────────────────────────── */
const AiDashboard = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations?.labels]);

  // Single source of truth for any API failure or status:false response.
  // We never surface raw server messages to the UI anymore.
  const genericError = useMemo(
    () => t.noDataAvailable || "No data available",
    [t],
  );

  /* ── Mount tracking — prevents stale state writes after unmount ── */
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /* ── Auth context ── */
  const user = TokenService.getUser();
  const role = user?.role || {};
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const class_id = clientData?.class_id || "";
  const section_id = clientData?.student_section || "";
  const student_id = clientData?.student_id || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const user_id = user?.id || "";
  const start_date = clientData?.start_date;
  const isParent = useMemo(() => role?.slug === "parent", [role]);
  // Whose data to fetch: parent → their child's id, student → their own id.
  const targetStudentId = useMemo(
    () => (isParent ? student_id : user_id),
    [isParent, student_id, user_id],
  );

  /* ── Safe state setter (mount + abort guarded) ── */
  const safeSet = useCallback((setter, value, controller) => {
    if (!isMountedRef.current) return;
    if (controller && controller.signal.aborted) return;
    setter(value);
  }, []);

  /* ── Abort refs ── */
  const perfAbortRef = useRef(null);
  const subjectsAbortRef = useRef(null);
  const predictionAbortRef = useRef(null);
  const attendanceAbortRef = useRef(null);
  const healthAbortRef = useRef(null);
  const calendarAbortRef = useRef(null);
  const assessmentAbortRef = useRef(null);
  const remedialAbortRef = useRef(null);
  const engagementAbortRef = useRef(null);
  const classAvgAbortRef = useRef(null);
  const alertsAbortRef = useRef(null);

  /* ── Performance Overview ── */
  const [performanceData, setPerformanceData] = useState([]);
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfError, setPerfError] = useState(null);

  /* ── Subjects + Score Prediction ── */
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [currentExams, setCurrentExams] = useState([]);
  const [predictionLoading, setPredictionLoading] = useState(false);
  const [predictionError, setPredictionError] = useState(null);

  /* ── Attendance Rate ── */
  const [filter, setFilter] = useState("All");
  const [attendanceData, setAttendanceData] = useState({
    overall: 0,
    presentDays: 0,
    workingDays: 0,
    absentDays: 0,
    filter: "All",
    academicStartDate: "",
  });
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState(null);

  /* ── Academic Health Score ── */
  const [healthData, setHealthData] = useState();
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState(null);

  /* ── Attendance Calendar ── */
  const [calendarMonth, setCalendarMonth] = useState(currentYyyymm());
  const [calendarData, setCalendarData] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState(null);

  /* ── Assessment & Remedial Trackers ── */
  const [assessmentTracker, setAssessmentTracker] = useState([]);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState(null);

  const [remedialTracker, setRemedialTracker] = useState([]);
  const [remedialLoading, setRemedialLoading] = useState(false);
  const [remedialError, setRemedialError] = useState(null);

  /* ── Subject Engagement Score ── */
  const [engagementData, setEngagementData] = useState([]);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [engagementError, setEngagementError] = useState(null);

  /* ── Class Average Engagement (line chart) ── */
  // Filter retained for future weekly/monthly/yearly toggle.
  const [classAvgFilter, setClassAvgFilter] = useState("weekly");
  const [classAvgChart, setClassAvgChart] = useState({
    labels: [],
    datasets: [],
  });
  const [classAvgLoading, setClassAvgLoading] = useState(false);
  const [classAvgError, setClassAvgError] = useState(null);

  /* ── Alerts ── */
  const [alertList, setAlertList] = useState({ positive: [], negative: [] });
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState(null);

  /* ==================================================================
   *  fetchPerformance
   * ================================================================== */
  const fetchPerformance = useCallback(async () => {
    if (!targetStudentId || !school_id) return;
    perfAbortRef.current?.abort();
    const controller = new AbortController();
    perfAbortRef.current = controller;
    safeSet(setPerfLoading, true, controller);
    safeSet(setPerfError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `dashboard/student/getStudentPerformance`,
        {
          user_id: targetStudentId,
          school_id,
          academic_id: academic_year_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (response?.data?.status && Array.isArray(response.data.data)) {
        safeSet(setPerformanceData, response.data.data, controller);
      } else {
        safeSet(setPerformanceData, [], controller);
        safeSet(setPerfError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setPerformanceData, [], controller);
        safeSet(setPerfError, genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setPerfLoading, false, controller);
      }
    }
  }, [targetStudentId, school_id, academic_year_id, genericError, safeSet]);

  useEffect(() => {
    fetchPerformance();
    return () => perfAbortRef.current?.abort();
  }, [fetchPerformance]);

  /* ==================================================================
   *  fetchHealthScore
   * ================================================================== */
  const fetchHealthScore = useCallback(async () => {
    if (!targetStudentId) return;
    healthAbortRef.current?.abort();
    const controller = new AbortController();
    healthAbortRef.current = controller;
    safeSet(setHealthLoading, true, controller);
    safeSet(setHealthError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `dashboard/student/healthScore`,
        {
          student_id: targetStudentId,
          from: moment(start_date).format("YYYY-MM-DD"),
          to: moment().format("YYYY-MM-DD"),
          academic_year: academic_year_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;

      const result = response?.data?.data || response?.data;
      if (result && Array.isArray(result.components)) {
        const breakdown = {
          examMarks: { weight: 0, score: 0 },
          attendance: { weight: 0, score: 0 },
          assessmentRemedial: { weight: 0, score: 0 },
          applicationUsage: { weight: 0, score: 0 },
        };
        result.components.forEach((c) => {
          const key = HEALTH_KEY_MAP[c.key];
          if (!key) return;
          breakdown[key] = {
            weight: Number(c.weight) || 0,
            score: Number(c.performance_pct) || 0,
          };
        });
        const compositeScore = Number(result.total_percentage) || 0;
        safeSet(
          setHealthData,
          {
            compositeScore,
            status: statusFromScore(compositeScore),
            breakdown,
          },
          controller,
        );
      } else {
        safeSet(setHealthError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) safeSet(setHealthError, genericError, controller);
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setHealthLoading, false, controller);
      }
    }
  }, [targetStudentId, start_date, academic_year_id, genericError, safeSet]);

  useEffect(() => {
    fetchHealthScore();
    return () => healthAbortRef.current?.abort();
  }, [fetchHealthScore]);

  /* ==================================================================
   *  fetchAttendance
   * ================================================================== */
  const fetchAttendance = useCallback(async () => {
    if (!targetStudentId) return;
    attendanceAbortRef.current?.abort();
    const controller = new AbortController();
    attendanceAbortRef.current = controller;
    safeSet(setAttendanceLoading, true, controller);
    safeSet(setAttendanceError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `/dashboard/student/getAttendanceRate`,
        {
          school_id,
          class_id,
          section_id,
          student_id: targetStudentId,
          filter: MONTH_LABEL_TO_NUMBER[filter] ?? "All",
          academic_id: academic_year_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const result = response?.data?.data || response?.data;
      if (result) {
        safeSet(
          setAttendanceData,
          {
            overall: Number(result.attendance_percent ?? 0),
            presentDays: Number(result.present_days ?? 0),
            workingDays: Number(result.total_days ?? 0),
            absentDays: Number(result.absent_days ?? 0),
            filter,
            academicStartDate: result.academic_start_date ?? "",
          },
          controller,
        );
      } else {
        safeSet(setAttendanceError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err))
        safeSet(setAttendanceError, genericError, controller);
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setAttendanceLoading, false, controller);
      }
    }
  }, [
    school_id,
    class_id,
    section_id,
    targetStudentId,
    filter,
    genericError,
    safeSet,
  ]);

  useEffect(() => {
    fetchAttendance();
    return () => attendanceAbortRef.current?.abort();
  }, [fetchAttendance]);

  /* ==================================================================
   *  fetchAssessmentTracker
   * ================================================================== */
  const fetchAssessmentTracker = useCallback(async () => {
    if (!targetStudentId) return;
    assessmentAbortRef.current?.abort();
    const controller = new AbortController();
    assessmentAbortRef.current = controller;
    safeSet(setAssessmentLoading, true, controller);
    safeSet(setAssessmentError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `dashboard/student/assessment-tracker`,
        { student_id: targetStudentId, academic_id: academic_year_id },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const ok = response?.data?.success ?? response?.data?.status;
      if (ok && Array.isArray(response.data.data)) {
        safeSet(setAssessmentTracker, response.data.data, controller);
      } else {
        safeSet(setAssessmentTracker, [], controller);
        safeSet(setAssessmentError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setAssessmentTracker, [], controller);
        safeSet(setAssessmentError, genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setAssessmentLoading, false, controller);
      }
    }
  }, [targetStudentId, genericError, safeSet]);

  useEffect(() => {
    fetchAssessmentTracker();
    return () => assessmentAbortRef.current?.abort();
  }, [fetchAssessmentTracker]);

  /* ==================================================================
   *  fetchRemedialTracker
   * ================================================================== */
  const fetchRemedialTracker = useCallback(async () => {
    if (!targetStudentId) return;
    remedialAbortRef.current?.abort();
    const controller = new AbortController();
    remedialAbortRef.current = controller;
    safeSet(setRemedialLoading, true, controller);
    safeSet(setRemedialError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `dashboard/student/remedial-tracker`,
        { student_id: targetStudentId, academic_id: academic_year_id },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const ok = response?.data?.success ?? response?.data?.status;
      if (ok && Array.isArray(response.data.data)) {
        safeSet(setRemedialTracker, response.data.data, controller);
      } else {
        safeSet(setRemedialTracker, [], controller);
        safeSet(setRemedialError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setRemedialTracker, [], controller);
        safeSet(setRemedialError, genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setRemedialLoading, false, controller);
      }
    }
  }, [targetStudentId, genericError, safeSet]);

  useEffect(() => {
    fetchRemedialTracker();
    return () => remedialAbortRef.current?.abort();
  }, [fetchRemedialTracker]);

  /* ==================================================================
   *  fetchEngagementScore
   * ================================================================== */
  const fetchEngagementScore = useCallback(async () => {
    if (!targetStudentId) return;
    engagementAbortRef.current?.abort();
    const controller = new AbortController();
    engagementAbortRef.current = controller;
    safeSet(setEngagementLoading, true, controller);
    safeSet(setEngagementError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `dashboard/student/subject-engagement-score`,
        { student_id: targetStudentId, academic_id: academic_year_id },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const ok = response?.data?.success ?? response?.data?.status;
      if (ok && Array.isArray(response.data.data)) {
        safeSet(setEngagementData, response.data.data, controller);
      } else {
        safeSet(setEngagementData, [], controller);
        safeSet(setEngagementError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setEngagementData, [], controller);
        safeSet(setEngagementError, genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setEngagementLoading, false, controller);
      }
    }
  }, [targetStudentId, genericError, safeSet]);

  useEffect(() => {
    fetchEngagementScore();
    return () => engagementAbortRef.current?.abort();
  }, [fetchEngagementScore]);

  /* ==================================================================
   *  fetchClassAvgEngagement
   * ================================================================== */
  const fetchClassAvgEngagement = useCallback(async () => {
    // class_id / academic_year_id can be missing in an older login token;
    // the API resolves them from the student record, so only the student id is required.
    if (!targetStudentId) return;
    classAvgAbortRef.current?.abort();
    const controller = new AbortController();
    classAvgAbortRef.current = controller;
    safeSet(setClassAvgLoading, true, controller);
    safeSet(setClassAvgError, null, controller);
    try {
      const meId = Number(targetStudentId);
      const response = await AxiosInstance.post(
        `dashboard/student/engagementScore`,
        {
          class_id: class_id || "",
          user_id: String(meId),
          academic_id: academic_year_id || "",
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const ok = response?.data?.success ?? response?.data?.status;
      if (ok && Array.isArray(response.data.data) && response.data.data.length) {
        const rows = response.data.data;
        const labels = [];
        const myData = [];
        const topperData = [];
        let myName = "";
        rows.forEach((row) => {
          // X-axis label intentionally blank — reserved for upcoming
          // weekly/monthly/yearly filter feature.
          labels.push(row.date || ""); // was labels.push("")
          const students = Array.isArray(row.student) ? row.student : [];
          const me = students.find((s) => Number(s.user_id) === meId);
          const topper = students.find((s) => Number(s.is_topper) === 1);
          if (me && !myName) {
            myName = (me.student_name || "").trim(); // ← first match wins
          }
          myData.push(me ? Number(me.score) || 0 : 0);
          topperData.push(topper ? Number(topper.score) || 0 : 0);
        });
        safeSet(
          setClassAvgChart,
          {
            labels,
            datasets: [
              { label: myName, data: myData },
              {
                label: t.classTopperScore || "Class Topper Score",
                data: topperData,
              },
            ],
          },
          controller,
        );
      } else {
        safeSet(setClassAvgChart, { labels: [], datasets: [] }, controller);
        safeSet(setClassAvgError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setClassAvgChart, { labels: [], datasets: [] }, controller);
        safeSet(setClassAvgError, genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setClassAvgLoading, false, controller);
      }
    }
  }, [class_id, academic_year_id, targetStudentId, t, genericError, safeSet]);

  useEffect(() => {
    fetchClassAvgEngagement();
    return () => classAvgAbortRef.current?.abort();
  }, [fetchClassAvgEngagement]);

  /* ==================================================================
   *  fetchAlertList
   * ================================================================== */
  const fetchAlertList = useCallback(async () => {
    if (!targetStudentId) return;
    alertsAbortRef.current?.abort();
    const controller = new AbortController();
    alertsAbortRef.current = controller;
    safeSet(setAlertsLoading, true, controller);
    safeSet(setAlertsError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `dashboard/getAlertList`,
        {
          school_id,
          class_id,
          section_id,
          user_id: targetStudentId,
          academic_id: academic_year_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const ok = response?.data?.success ?? response?.data?.status;
      const result = response?.data?.data;
      if (ok && result) {
        safeSet(
          setAlertList,
          {
            positive: Array.isArray(result.positive) ? result.positive : [],
            negative: Array.isArray(result.negative) ? result.negative : [],
          },
          controller,
        );
      } else {
        safeSet(setAlertList, { positive: [], negative: [] }, controller);
        safeSet(setAlertsError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setAlertList, { positive: [], negative: [] }, controller);
        safeSet(setAlertsError, genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setAlertsLoading, false, controller);
      }
    }
  }, [
    school_id,
    class_id,
    section_id,
    targetStudentId,
    academic_year_id,
    genericError,
    safeSet,
  ]);

  useEffect(() => {
    fetchAlertList();
    return () => alertsAbortRef.current?.abort();
  }, [fetchAlertList]);

  /* ==================================================================
   *  fetchClassSubjects
   * ================================================================== */
  const fetchClassSubjects = useCallback(async () => {
    if (!school_id || !class_id || !section_id) return;
    subjectsAbortRef.current?.abort();
    const controller = new AbortController();
    subjectsAbortRef.current = controller;
    try {
      const response = await AxiosInstance.post(
        `school/class/subjects`,
        {
          class_id,
          section: section_id,
          school_id,
          academic_id: academic_year_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const ok = response?.data?.success ?? response?.data?.status;
      if (ok && Array.isArray(response.data.data)) {
        const list = response.data.data.map((s) => ({
          subject_id: s.subject_id,
          subject: s.subject_name
            ? s.subject_name.charAt(0) + s.subject_name.slice(1).toLowerCase()
            : "",
        }));
        if (!isMountedRef.current || controller.signal.aborted) return;
        setSubjects(list);
        if (list.length > 0) {
          setSelectedSubjectId((prev) => prev ?? list[0].subject_id);
        }
      }
    } catch (err) {
      // Silent failure — score prediction card will simply show empty tabs.
      if (!isAbortError(err)) {
        // intentionally swallowed; UI degrades gracefully
      }
    }
  }, [school_id, class_id, section_id]);

  useEffect(() => {
    fetchClassSubjects();
    return () => subjectsAbortRef.current?.abort();
  }, [fetchClassSubjects]);

  const handleSubjectChange = useCallback(
    (selected) => {
      const subjectName =
        typeof selected === "string" ? selected : selected?.subject;
      const match = subjects.find(
        (s) => s.subject?.toLowerCase() === subjectName?.toLowerCase(),
      );
      if (match && match.subject_id !== selectedSubjectId) {
        setSelectedSubjectId(match.subject_id);
      }
    },
    [subjects, selectedSubjectId],
  );

  /* ==================================================================
   *  fetchScorePrediction
   * ================================================================== */
  const fetchScorePrediction = useCallback(async () => {
    if (!selectedSubjectId || !targetStudentId) return;
    predictionAbortRef.current?.abort();
    const controller = new AbortController();
    predictionAbortRef.current = controller;
    safeSet(setPredictionLoading, true, controller);
    safeSet(setPredictionError, null, controller);
    try {
      const response = await AxiosInstance.post(
        `dashboard/ai-score-prediction`,
        {
          student_id: targetStudentId,
          subject_id: selectedSubjectId,
          academic_id: academic_year_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const ok = response?.data?.success ?? response?.data?.status;
      if (ok && Array.isArray(response.data.data)) {
        const exams = response.data.data.map((e) => ({
          exam_name: e.exam_name,
          predicted: e.ai_predicted_score ?? e.manual_predicted_score ?? null,
          actual: e.actual_score ?? null,
        }));
        safeSet(setCurrentExams, exams, controller);
      } else {
        safeSet(setCurrentExams, [], controller);
        safeSet(setPredictionError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        safeSet(setCurrentExams, [], controller);
        safeSet(setPredictionError, genericError, controller);
      }
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setPredictionLoading, false, controller);
      }
    }
  }, [selectedSubjectId, targetStudentId, genericError, safeSet]);

  // Single effect — the previous version had this declared twice, doubling
  // the API hit on every subject change.
  useEffect(() => {
    fetchScorePrediction();
    return () => predictionAbortRef.current?.abort();
  }, [fetchScorePrediction]);

  /* ==================================================================
   *  fetchCalendar
   * ================================================================== */
  const fetchCalendar = useCallback(async () => {
    if (!targetStudentId || !calendarMonth) return;
    calendarAbortRef.current?.abort();
    const controller = new AbortController();
    calendarAbortRef.current = controller;
    safeSet(setCalendarLoading, true, controller);
    safeSet(setCalendarError, null, controller);
    try {
      const [, monthStr] = calendarMonth.split("-");
      const response = await AxiosInstance.post(
        `/dashboard/getAttendanceCalendar`,
        {
          school_id,
          class_id,
          section_id,
          user_id: targetStudentId,
          month: Number(monthStr),
          academic_id: academic_year_id,
        },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      const result = response?.data?.data || response?.data;
      if (result && Array.isArray(result.calendar)) {
        const monthKey =
          result.month && result.year
            ? `${result.year}-${pad2(result.month)}`
            : calendarMonth;
        const summary = result.summary || {};
        const workingDays =
          Number(summary.present ?? 0) +
          Number(summary.absent ?? 0) +
          Number(summary.leave ?? 0);
        const days = result.calendar
          .map((d) => ({
            date: d.date,
            status: STATUS_CODE_MAP[d.status] || null,
          }))
          .filter((d) => d.date && d.status);
        safeSet(
          setCalendarData,
          {
            studentId: targetStudentId,
            month: monthKey,
            summary: {
              workingDays,
              present: Number(summary.present ?? 0),
              absent: Number(summary.absent ?? 0),
              leave: Number(summary.leave ?? 0),
              holiday: Number(summary.holiday ?? 0),
              weekend: Number(summary.weekend ?? 0),
            },
            days,
          },
          controller,
        );
      } else {
        safeSet(setCalendarError, genericError, controller);
      }
    } catch (err) {
      if (!isAbortError(err))
        safeSet(setCalendarError, genericError, controller);
    } finally {
      if (!controller.signal.aborted) {
        safeSet(setCalendarLoading, false, controller);
      }
    }
  }, [
    school_id,
    class_id,
    section_id,
    targetStudentId,
    academic_year_id,
    calendarMonth,
    genericError,
    safeSet,
  ]);

  useEffect(() => {
    fetchCalendar();
    return () => calendarAbortRef.current?.abort();
  }, [fetchCalendar]);

  /* ==================================================================
   *  Derived data
   * ================================================================== */
  const buildTrackerChart = useCallback(
    (rows) => {
      if (!Array.isArray(rows) || rows.length === 0) {
        return { labels: [], datasets: [] };
      }
      return {
        labels: rows.map((r) => r.subject),
        datasets: [
          {
            label: t.passed || "Passed",
            data: rows.map((r) => Number(r.pass_count) || 0),
          },
          {
            label: t.failed || "Failed",
            data: rows.map((r) => Number(r.fail_count) || 0),
          },
          {
            label: t.unattended || "Unattended",
            data: rows.map((r) => Number(r.total_unattended) || 0),
          },
        ],
      };
    },
    [t],
  );

  const assessmentChart = useMemo(
    () => buildTrackerChart(assessmentTracker),
    [assessmentTracker, buildTrackerChart],
  );

  const remedialChart = useMemo(
    () => buildTrackerChart(remedialTracker),
    [remedialTracker, buildTrackerChart],
  );

  const engagementChartData = useMemo(
    () =>
      (engagementData || []).map((item) => ({
        exam_name: item.subject,
        overAllMark: (Number(item.engagement_score) || 0) / 10,
        subjects: [],
      })),
    [engagementData],
  );

  const scorePredictionChartData = useMemo(() => {
    if (subjects.length === 0) return [];
    return subjects.map((s) => ({
      subject: s.subject,
      subject_id: s.subject_id,
      exams:
        String(s.subject_id) === String(selectedSubjectId) ? currentExams : [],
    }));
  }, [subjects, selectedSubjectId, currentExams]);

  const classAvgFilterOptions = useMemo(
    () => [
      { value: "weekly", label: t.weekly || "Weekly" },
      { value: "monthly", label: t.monthly || "Monthly" },
      { value: "yearly", label: t.yearly || "Yearly" },
    ],
    [t],
  );

  const onTrackAlerts = useMemo(
    () =>
      alertList.positive.map((item, idx) => {
        const isAttendance = item.title === "Attendance";
        return {
          id: `ot-${idx}`,
          color: "green",
          message: item.message,
          icon: isAttendance ? (
            <i
              className="fa-regular fa-clock"
              style={{ color: "var(--color-neutral-600)" }}
            />
          ) : (
            <i
              className="fa-solid fa-circle-check"
              style={{ color: "var(--green-dark)" }}
            />
          ),
        };
      }),
    [alertList.positive],
  );

  const needsAttentionAlerts = useMemo(
    () =>
      alertList.negative.map((item, idx) => ({
        id: `na-${idx}`,
        color: "orange",
        message: item.message,
        icon: (
          <i
            className="fa-solid fa-triangle-exclamation"
            style={{ color: "var(--orange-dark)" }}
          />
        ),
      })),
    [alertList.negative],
  );

  const handleViewAllAssessment = useCallback(
    () => navigate("/user/assessment", { state: { activeTab: 0 } }),
    [navigate],
  );

  const handleViewAllRemedial = useCallback(
    () => navigate("/user/assessment", { state: { activeTab: 1 } }),
    [navigate],
  );

  /* ==================================================================
   *  Initial dashboard loader — shown only on first render before any
   *  primary data has arrived. After that, per-card loaders take over.
   * ================================================================== */
  const isInitialLoading =
    perfLoading && performanceData.length === 0 && !perfError;

  /* ==================================================================
   *  Render
   * ================================================================== */
  if (isInitialLoading) {
    return (
      <div className={academicStyles.container}>
        <div
          className={dashStyles.card}
          style={{ minHeight: 400, position: "relative" }}
        >
          <CustomLoader label={t.loading} />
        </div>
      </div>
    );
  }

  return (
    <div className={academicStyles.container}>
      <div
        className={dashStyles.headerLine}
        style={{ justifyContent: "space-between" }}
      >
        <div className={dashStyles.leftSection}>
          <h1 className={dashStyles.headerTitle}>  Student Dashboard  </h1>
        </div>
      </div>
      <div className={dashStyles.row}>
        {/* Performance Overview */}
        <div className={`${dashStyles.col} ${dashStyles.col8}`}>
          <div className={dashStyles.card}>
            <CustomBarChart
              icon={<i className="fa-solid fa-chart-column"></i>}
              title={t.performanceOverview || "Performance Overview"}
              datasetLabel={t.dataset1 || "Dataset 1"}
              data={performanceData}
              loading={perfLoading}
              error={perfError}
              bottomHint={true}
              info="Click an exam bar to view subject-wise scores; hover for exact values."
            />
          </div>
        </div>

        {/* Academic Health Score */}
        <div className={`${dashStyles.col} ${dashStyles.col4}`}>
          <div className={dashStyles.card}>
            <CustomDonutChart
              data={healthData}
              loading={healthLoading}
              error={healthError}
              title={t.academicHealthScore || "Academic Health Score"}
            />
          </div>
        </div>

        {/* Attendance Rate */}
        <div className={`${dashStyles.col} ${dashStyles.col6}`}>
          <div className={dashStyles.card}>
            <CustomDonutChart
              data={attendanceData}
              filterOptions={FILTER_OPTIONS}
              onFilterChange={setFilter}
              loading={attendanceLoading}
              error={attendanceError}
              title={t.attendanceRate || "Attendance Rate"}
            />
          </div>
        </div>

        {/* Attendance Calendar */}
        <div className={`${dashStyles.col} ${dashStyles.col6}`}>
          <div className={dashStyles.card}>
            <CustomCalendar
              month={calendarMonth}
              data={calendarData}
              loading={calendarLoading}
              error={calendarError}
              onMonthChange={(m) => setCalendarMonth(m)}
            />
          </div>
        </div>

        {/* Subject wise Engagement Score */}
        <div className={`${dashStyles.col} ${dashStyles.col6}`}>
          <div className={dashStyles.card}>
            <CustomBarChart
              icon={<i className="fa-solid fa-rocket"></i>}
              title={
                t.subjectWiseEngagementScore || "Subject wise Engagement Score"
              }
              maxY={10}
              stepSize={1}
              valueLabel={t.score || "Score"}
              datasetLabel={t.engagementScore || "Engagement Score"}
              data={engagementChartData}
              loading={engagementLoading}
              error={engagementError}
              bottomHint={false}
            />
          </div>
        </div>

        {/* Subject-Wise Score Prediction */}
        <div className={`${dashStyles.col} ${dashStyles.col6}`}>
          <div className={dashStyles.card}>
            <ScorePredictionChart
              icon={<i className="fa-solid fa-wand-magic-sparkles"></i>}
              title={
                t.subjectWiseScorePrediction || "Subject wise Score Prediction"
              }
              data={scorePredictionChartData}
              loading={
                predictionLoading && scorePredictionChartData.length === 0
              }
              chartLoading={predictionLoading}
              error={predictionError}
              predictedLabel={t.predictedScore || "Predicted Score"}
              actualLabel={t.actualScore || "Actual Score"}
              onSubjectChange={handleSubjectChange}
              maxY={100}
            />
          </div>
        </div>

        {/* Assessment Progress Tracker */}
        <div className={`${dashStyles.col} ${dashStyles.col6}`}>
          <div className={dashStyles.card}>
            <HorizontalBarChart
              title={
                t.assessmentProgressTracker || "Assessment Progress Tracker"
              }
              icon={<FaChartLine />}
              labels={assessmentChart.labels}
              datasets={assessmentChart.datasets}
              loading={assessmentLoading}
              error={assessmentError}
              showViewAll={!isParent}
              viewAllLabel={t.viewAll || "View All"}
              onViewAll={handleViewAllAssessment}
              emptyMessage={t.noExamDataAvailable || "No data available"}
              height={320}
              valueSuffix=""
            />
          </div>
        </div>

        {/* Remedial Progress Tracker */}
        <div className={`${dashStyles.col} ${dashStyles.col6}`}>
          <div className={dashStyles.card}>
            <HorizontalBarChart
              title={t.remedialProgressTracker || "Remedial Progress Tracker"}
              icon={<FaChartLine />}
              labels={remedialChart.labels}
              datasets={remedialChart.datasets}
              loading={remedialLoading}
              error={remedialError}
              showViewAll={!isParent}
              viewAllLabel={t.viewAll || "View All"}
              onViewAll={handleViewAllRemedial}
              emptyMessage={t.noExamDataAvailable || "No data available"}
              height={320}
              valueSuffix=""
            />
          </div>
        </div>

        {/* Class Average Engagement Score */}
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            <LineChart
              icon={<i className="fa-solid fa-chart-line"></i>}
              title={
                t.classAverageEngagementScore ||
                "Class Average Engagement Score"
              }
              showInfoIcon={false}
              labels={classAvgChart.labels}
              datasets={classAvgChart.datasets}
              loading={classAvgLoading}
              error={classAvgError}
              filterOptions={classAvgFilterOptions}
              activeFilter={classAvgFilter}
              onFilterChange={setClassAvgFilter}
              showFilters={false}
              emptyMessage={t.noExamDataAvailable || "No data available"}
              height={320}
              yMin={0}
              yMax={100}
            />
          </div>
        </div>

        {/* On Track Alerts */}
        <div className={`${dashStyles.col} ${dashStyles.col4}`}>
          <div className={dashStyles.card}>
            <AlertsList
              title={t.onTrackAlerts || "On Track Alerts"}
              icon={<i className="fa-regular fa-bell" />}
              items={onTrackAlerts}
              loading={alertsLoading}
              error={alertsError}
              emptyMessage={t.noAlerts || "No alerts to display"}
              maxHeight={460}
            />
          </div>
        </div>

        {/* Needs Attention Alerts */}
        <div className={`${dashStyles.col} ${dashStyles.col8}`}>
          <div className={dashStyles.card}>
            <AlertsList
              title={t.needsAttentionAlerts || "Needs Attention Alerts"}
              icon={<i className="fa-regular fa-bell" />}
              items={needsAttentionAlerts}
              loading={alertsLoading}
              error={alertsError}
              emptyMessage={t.noAlerts || "No alerts to display"}
              maxHeight={460}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiDashboard;
